import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Typography } from "@mui/material";
import {
  Plus, Edit2, Trash2, Eye, X,
  Mail, Phone, Hash, Calendar, Clock,
  User, Briefcase, Search
} from "lucide-react";
import styles from "./interviews.module.scss";
import OfferLetterGenerator from "./OfferLetterGenerator";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../../../../services/config';
const API_BASE = apiBaseUrl;

const TYPES    = ["Technical","HR","Initial Screening","Final"];
const STATUSES = ["Scheduled","Completed","Cancelled","No Show","SELECTED","NOT-SELECTED","ONHOLD"];

function useStatusColor() {
  const { T } = useTheme();
  return {
    Scheduled:     T.cyan,
    Completed:     T.green,
    Cancelled:     T.red,
    "No Show":     T.amber,
    SELECTED:      T.green,
    "NOT-SELECTED":T.red,
    ONHOLD:        T.amber,
  };
}
function useTypeColor() {
  const { T } = useTheme();
  return { Technical:T.cyan, HR:T.purple, "Initial Screening":T.amber, Final:T.green };
}
function useInp() {
  const { T } = useTheme();
  return (extra={}) => ({
    width:"100%", padding:"9px 12px", border:`1px solid ${T.border}`,
    borderRadius:8, background:T.inp_bg, color:T.text,
    fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none",
    boxSizing:"border-box", ...extra,
  });
}
function useLbl() {
  const { T } = useTheme();
  return {
    fontSize:11, fontWeight:700, color:T.muted,
    textTransform:"uppercase", letterSpacing:"0.07em",
    display:"block", marginBottom:5,
  };
}

const EMPTY = {
  first_name:"", last_name:"", email:"", phone:"", id_number:"",
  position:"", interview_type:"Technical", interview_date:"",
  interview_time:"", interviewer_id:"", status:"Scheduled",
  notes:"", rating:""
};

// ── Build meeting URLs for an interview ──────────────────────────
const buildLinks = (iv, techId) => {
  const base  = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
  const room  = iv.room_name || `hirality_${iv.id}`;
  // Pass token in URL so new tab can authenticate (sessionStorage is tab-scoped)
  const token = sessionStorage.getItem("platform_token") ||
                localStorage.getItem("platform_token")   || "";
  const params = `type=interview&roomId=${room}&techId=${techId}&interviewId=${iv.id}&token=${encodeURIComponent(token)}`;
  return {
    interviewer: `${base}/session-room?clientId=interviewer&${params}`,
    candidate:   `${base}/session-room?clientId=candidate&${params}`,
  };
};

// ── Meeting links popup ──────────────────────────────────────────
const MeetingLinksModal = ({ iv, techId, onClose }) => {
  const { T } = useTheme();
  const [copied, setCopied] = React.useState("");
  const links = buildLinks(iv, techId);

  const copy = (key, url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 2500);
    });
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      backdropFilter:"blur(4px)" }}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
        borderRadius:14, width:"100%", maxWidth:520,
        boxShadow:T.shadow, overflow:"hidden" }}>

        <div style={{ padding:"18px 22px 14px",
          background:`linear-gradient(135deg,${T.green}14,transparent)`,
          borderBottom:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:11, color:T.green, fontWeight:700,
              letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>
              MEETING LINKS
            </div>
            <h3 style={{ fontSize:15, fontWeight:800, margin:0, color:T.text }}>
              {iv.first_name} {iv.last_name}
            </h3>
          </div>
          <button onClick={onClose}
            style={{ border:"none", background:T.toggle_bg,
              borderRadius:"50%", width:30, height:30, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", color:T.muted }}>
            <X size={14}/>
          </button>
        </div>

        <div style={{ padding:"20px 22px", display:"flex", flexDirection:"column", gap:14 }}>
          {[
            { key:"interviewer", label:"Interviewer Link", desc:"Shows questions & evaluation panel",
              color:T.cyan,  icon:"🎙️" },
            { key:"candidate",   label:"Candidate Link",  desc:"Shows meeting only — no questions",
              color:T.green, icon:"👤" },
          ].map(({ key, label, desc, color, icon }) => (
            <div key={key} style={{ background:T.inp_bg,
              border:`1px solid ${key==="interviewer"?`${T.cyan}26`:`${T.green}26`}`,
              borderRadius:10, padding:"14px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:12.5, fontWeight:700, color,
                    display:"flex", alignItems:"center", gap:5 }}>
                    {icon} {label}
                  </div>
                  <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{desc}</div>
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button
                    onClick={() => window.open(links[key], "_blank")}
                    style={{ padding:"5px 10px", borderRadius:7,
                      border:`1px solid ${color}30`, background:`${color}10`,
                      color, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                      fontSize:11.5, fontWeight:700 }}>
                    Join
                  </button>
                  <button
                    onClick={() => copy(key, links[key])}
                    style={{ padding:"5px 10px", borderRadius:7,
                      border:`1px solid ${T.border}`, background:"none",
                      color:copied===key ? T.green : T.muted, cursor:"pointer",
                      fontFamily:"Outfit,sans-serif", fontSize:11.5, fontWeight:700 }}>
                    {copied===key ? "✓ Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div style={{ fontSize:11, color:T.dim,
                fontFamily:"'JetBrains Mono',monospace",
                wordBreak:"break-all", lineHeight:1.5 }}>
                {links[key]}
              </div>
            </div>
          ))}

          <div style={{ fontSize:11.5, color:T.muted, padding:"8px 12px",
            background:`${T.cyan}0A`, borderRadius:8,
            border:`1px solid ${T.border}` }}>
            💡 Share the <strong style={{color:T.green}}>Candidate Link</strong> with the candidate
            before the interview. They will see only the meeting — questions stay hidden.
          </div>
        </div>
      </div>
    </div>
  );
};

const StarRating = ({ value, onChange }) => {
  const { T } = useTheme();
  return (
    <div style={{ display:"flex", gap:4 }}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} type="button" onClick={()=>onChange(n)}
          style={{ border:"none", background:"none", cursor:"pointer",
            fontSize:22, color:n<=(value||0)?T.amber:T.dim, padding:"2px" }}>★</button>
      ))}
    </div>
  );
};

const Section = ({title, children}) => {
  const { T } = useTheme();
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.cyan,
        textTransform:"uppercase", letterSpacing:"0.1em",
        marginBottom:12, paddingBottom:6, borderBottom:`1px solid ${T.border}` }}>
        {title}
      </div>
      {children}
    </div>
  );
};

const Grid2 = ({children}) => (
  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
    {children}
  </div>
);

/* ── Status badge using existing SCSS ── */
const StatusBadge = ({ status }) => {
  const map = {
    Scheduled: styles.pending,
    Completed: styles.selected,
    Cancelled: styles.rejected,
    "No Show": styles.onhold,
  };
  return <span className={`${styles.badge} ${map[status]||styles.pending}`}>{status}</span>;
};

/* ── View detail panel ── */
const ViewPanel = ({ iv, onClose, onEdit }) => {
  const { T } = useTheme();
  const TYPE_COLOR = useTypeColor();
  const STATUS_COLOR = useStatusColor();
  if(!iv) return null;
  const InfoRow = ({label, val, icon}) => !val ? null : (
    <div style={{ display:"flex", gap:12, marginBottom:10,
      padding:"10px 14px", background:T.inp_bg,
      borderRadius:9, border:`1px solid ${T.border}` }}>
      <div style={{ color:T.cyan, flexShrink:0, paddingTop:2 }}>{icon}</div>
      <div>
        <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase",
          letterSpacing:"0.06em", fontWeight:700, marginBottom:2 }}>{label}</div>
        <div style={{ fontSize:13.5, color:T.text, fontWeight:600 }}>{val}</div>
      </div>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:16, backdropFilter:"blur(5px)" }}
      onClick={e=>{if(e.target===e.currentTarget) onClose();}}>
      <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
        borderRadius:16, width:"100%", maxWidth:520,
        maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column",
        boxShadow:T.shadow }}>

        <div style={{ padding:"20px 24px 14px", borderBottom:`1px solid ${T.border}`,
          background:`linear-gradient(135deg,${T.cyan}0F,transparent)`,
          display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:20, fontWeight:900, color:T.text, marginBottom:6 }}>
              {iv.first_name} {iv.last_name}
            </div>
            <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
              {[
                {l:iv.interview_type, c:TYPE_COLOR[iv.interview_type]||T.muted},
                {l:iv.status,         c:STATUS_COLOR[iv.status]||T.muted},
                iv.position && {l:iv.position, c:T.muted},
              ].filter(Boolean).map(({l,c})=>(
                <span key={l} style={{ fontSize:11, padding:"3px 10px", borderRadius:100,
                  background:`${c}18`, color:c, fontWeight:700 }}>{l}</span>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button
              onClick={()=>{ onClose(); setLinkIv(iv); }}
              style={{ padding:"7px 14px", borderRadius:8,
                border:`1px solid ${T.green}40`, background:`${T.green}0F`,
                color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:5 }}>
              📹 Meeting Links
            </button>
            <button onClick={()=>{ onClose(); onEdit(iv); }}
              style={{ padding:"7px 14px", borderRadius:8,
                border:`1px solid ${T.cyan}40`, background:"none",
                color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:4 }}>
              <Edit2 size={11}/> Edit
            </button>
            <button onClick={onClose}
              style={{ border:"none", background:T.toggle_bg,
                borderRadius:"50%", width:32, height:32, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", color:T.muted }}>
              <X size={15}/>
            </button>
          </div>
        </div>

        <div style={{ padding:"20px 24px", overflowY:"auto", flex:1 }}>
          <InfoRow label="Email"       val={iv.email}            icon={<Mail size={13}/>}/>
          <InfoRow label="Phone"       val={iv.phone}            icon={<Phone size={13}/>}/>
          <InfoRow label="ID Number"   val={iv.id_number}        icon={<Hash size={13}/>}/>
          <InfoRow label="Position"    val={iv.position}         icon={<Briefcase size={13}/>}/>
          <InfoRow label="Date"        val={iv.interview_date}   icon={<Calendar size={13}/>}/>
          <InfoRow label="Time"        val={iv.interview_time}   icon={<Clock size={13}/>}/>
          <InfoRow label="Interviewer" val={iv.interviewer_name} icon={<User size={13}/>}/>
          {iv.rating && (
            <div style={{ marginBottom:10, padding:"10px 14px",
              background:T.inp_bg, borderRadius:9, border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase",
                letterSpacing:"0.06em", fontWeight:700, marginBottom:6 }}>Rating</div>
              <div style={{ display:"flex", gap:3 }}>
                {[1,2,3,4,5].map(n=>(
                  <span key={n} style={{ fontSize:20, color:n<=iv.rating?T.amber:T.dim }}>★</span>
                ))}
              </div>
            </div>
          )}
          {iv.notes && (
            <div style={{ marginBottom:10, padding:"12px 14px",
              background:T.inp_bg, borderRadius:9, border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase",
                letterSpacing:"0.06em", fontWeight:700, marginBottom:6 }}>Notes</div>
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.65, whiteSpace:"pre-wrap" }}>
                {iv.notes}
              </div>
            </div>
          )}
          {iv.feedback && (
            <div style={{ padding:"12px 14px", background:`${T.green}0D`,
              border:`1px solid ${T.green}26`, borderLeft:`4px solid ${T.green}`,
              borderRadius:9 }}>
              <div style={{ fontSize:10.5, color:T.green, textTransform:"uppercase",
                letterSpacing:"0.06em", fontWeight:700, marginBottom:6 }}>Feedback</div>
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.65, whiteSpace:"pre-wrap" }}>
                {iv.feedback}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Employee Autocomplete ── */
const EmployeeAutocomplete = ({ employees, value, onChange }) => {
  const { T } = useTheme();
  const [query,    setQuery]    = React.useState("");
  const [open,     setOpen]     = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const wrapRef = React.useRef(null);

  // Sync selected from value prop (for edit pre-fill)
  React.useEffect(()=>{
    if(value && employees.length){
      const found = employees.find(e=>String(e.id)===String(value));
      if(found){ setSelected(found); setQuery(found.name); }
    } else if(!value){
      setSelected(null); setQuery("");
    }
  },[value, employees]);

  // Close on outside click
  React.useEffect(()=>{
    const handler = (e)=>{ if(wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);

  const filtered = employees.filter(e=>
    !query || e.name.toLowerCase().includes(query.toLowerCase()) ||
    (e.designation||"").toLowerCase().includes(query.toLowerCase()) ||
    (e.email||"").toLowerCase().includes(query.toLowerCase())
  ).slice(0,10);

  const select = (emp) => {
    setSelected(emp); setQuery(emp.name); setOpen(false);
    onChange(emp.id);
  };

  const clear = () => {
    setSelected(null); setQuery(""); onChange("");
  };

  return (
    <div ref={wrapRef} style={{ position:"relative" }}>
      {/* Input */}
      <div style={{ position:"relative" }}>
        <input
          value={query}
          onChange={e=>{ setQuery(e.target.value); setOpen(true); onChange(""); setSelected(null); }}
          onFocus={()=>setOpen(true)}
          placeholder="Search employee by name or designation..."
          style={{ width:"100%", padding:"9px 36px 9px 12px",
            border:`1px solid ${open ? T.cyan : T.border}`,
            borderRadius:8, background:T.inp_bg, color:T.text,
            fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none",
            boxSizing:"border-box",
            boxShadow: open ? `0 0 0 3px ${T.cyan}14` : "none",
            transition:"border-color .2s, box-shadow .2s" }}/>
        {query && (
          <button type="button" onClick={clear}
            style={{ position:"absolute", right:10, top:"50%",
              transform:"translateY(-50%)", border:"none", background:"none",
              cursor:"pointer", color:T.dim, fontSize:16, lineHeight:1 }}>✕</button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
          background:T.card, border:`1px solid ${T.cyan}33`,
          borderRadius:10, zIndex:999, maxHeight:220, overflowY:"auto",
          boxShadow:T.shadow }}>
          {filtered.length===0 ? (
            <div style={{ padding:"14px 16px", fontSize:13, color:T.dim,
              textAlign:"center" }}>
              {employees.length===0 ? "No employees loaded yet" : "No match found"}
            </div>
          ) : filtered.map(emp=>(
            <div key={emp.id} onClick={()=>select(emp)}
              style={{ padding:"10px 14px", cursor:"pointer",
                borderBottom:`1px solid ${T.cyan}0F`,
                transition:"background .15s",
                background: selected?.id===emp.id ? `${T.cyan}14` : "transparent" }}
              onMouseEnter={e=>e.currentTarget.style.background=`${T.cyan}0F`}
              onMouseLeave={e=>e.currentTarget.style.background=
                selected?.id===emp.id ? `${T.cyan}14`:"transparent"}>
              <div style={{ fontSize:13.5, fontWeight:700, color:T.text }}>{emp.name}</div>
              {emp.designation && (
                <div style={{ fontSize:11.5, color:T.muted, marginTop:1 }}>{emp.designation}</div>
              )}
              {emp.email && (
                <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>{emp.email}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selected employee email display */}
      {selected && (
        <div style={{ marginTop:8, padding:"8px 12px",
          background:`${T.cyan}0F`,
          border:`1px solid ${T.cyan}26`,
          borderRadius:7, display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:"50%",
            background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:11, fontWeight:800, color:"#fff", flexShrink:0 }}>
            {selected.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:12.5, fontWeight:700, color:T.text }}>{selected.name}</div>
            {selected.email && (
              <div style={{ fontSize:11.5, color:T.cyan, marginTop:1 }}>{selected.email}</div>
            )}
            {selected.designation && (
              <div style={{ fontSize:11, color:T.muted, marginTop:1 }}>{selected.designation}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Schedule / Edit Modal (same pattern as standalone Interviews.jsx) ── */
const ScheduleModal = ({ mode, data, employees, techName, onClose, onSave, saving }) => {
  const { T, theme } = useTheme();
  const inp = useInp();
  const lbl = useLbl();
  const [form, setForm] = useState(data || EMPTY);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{ setForm(data || EMPTY); }, [data]);

  const handleSave = () => onSave(form);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:16, backdropFilter:"blur(5px)" }}
      onClick={e=>{if(e.target===e.currentTarget) onClose();}}>
      <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
        borderRadius:16, width:"100%", maxWidth:700,
        maxHeight:"92vh", overflow:"hidden", display:"flex", flexDirection:"column",
        boxShadow:T.shadow }}>

        {/* Header */}
        <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${T.border}`,
          background:`linear-gradient(135deg,${T.cyan}0F,transparent)`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          flexShrink:0 }}>
          <div>
            <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
              letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>
              {mode==="create"?"SCHEDULE INTERVIEW":"EDIT INTERVIEW"}
            </div>
            <h3 style={{ fontSize:16, fontWeight:800, margin:0, color:T.text }}>
              {mode==="create"?"New Interview Schedule":"Update Interview"}
              {techName && <span style={{ color:T.cyan }}> — {techName}</span>}
            </h3>
          </div>
          <button onClick={onClose}
            style={{ border:"none", background:T.toggle_bg,
              borderRadius:"50%", width:32, height:32, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", color:T.muted }}>
            <X size={15}/>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ padding:24, overflowY:"auto", flex:1 }}>

          <Section title="👤 Candidate Information">
            <Grid2>
              <div>
                <label style={lbl}>First Name *</label>
                <input value={form.first_name}
                  onChange={e=>set("first_name",e.target.value)}
                  placeholder="First name" style={inp()}/>
              </div>
              <div>
                <label style={lbl}>Last Name *</label>
                <input value={form.last_name}
                  onChange={e=>set("last_name",e.target.value)}
                  placeholder="Last name" style={inp()}/>
              </div>
            </Grid2>
            <Grid2>
              <div>
                <label style={lbl}>Email</label>
                <input type="email" value={form.email}
                  onChange={e=>set("email",e.target.value)}
                  placeholder="candidate@email.com" style={inp()}/>
              </div>
              <div>
                <label style={lbl}>Phone Number</label>
                <input value={form.phone}
                  onChange={e=>set("phone",e.target.value)}
                  placeholder="+91 98765 43210" style={inp()}/>
              </div>
            </Grid2>
            <Grid2>
              <div>
                <label style={lbl}>ID Number</label>
                <input value={form.id_number}
                  onChange={e=>set("id_number",e.target.value)}
                  placeholder="Aadhaar / PAN / Passport" style={inp()}/>
              </div>
              <div>
                <label style={lbl}>Applying For</label>
                <input value={form.position}
                  onChange={e=>set("position",e.target.value)}
                  placeholder="e.g. Senior React Developer" style={inp()}/>
              </div>
            </Grid2>
          </Section>

          <Section title="📅 Interview Details">
            <Grid2>
              <div>
                <label style={lbl}>Interview Type</label>
                <select value={form.interview_type}
                  onChange={e=>set("interview_type",e.target.value)}
                  style={inp({cursor:"pointer"})}>
                  {TYPES.map(t=><option key={t} style={{background:T.opt_bg}}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Status</label>
                <select value={form.status}
                  onChange={e=>set("status",e.target.value)}
                  style={inp({cursor:"pointer"})}>
                  {STATUSES.map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
                </select>
              </div>
            </Grid2>
            <Grid2>
              <div>
                <label style={lbl}>
                  <Calendar size={10} style={{display:"inline",marginRight:4}}/>
                  Interview Date
                </label>
                <input type="date" value={form.interview_date}
                  onChange={e=>set("interview_date",e.target.value)}
                  style={{ ...inp(), colorScheme:theme }}/>
              </div>
              <div>
                <label style={lbl}>
                  <Clock size={10} style={{display:"inline",marginRight:4}}/>
                  Interview Time
                </label>
                <input type="time" value={form.interview_time}
                  onChange={e=>set("interview_time",e.target.value)}
                  style={{ ...inp(), colorScheme:theme }}/>
              </div>
            </Grid2>
            <div>
              <label style={lbl}>
                <User size={10} style={{display:"inline",marginRight:4}}/>
                Interviewer (from Employees)
              </label>
              <EmployeeAutocomplete
                employees={employees}
                value={form.interviewer_id}
                onChange={v=>set("interviewer_id",v)}
              />
              {employees.length===0 && (
                <div style={{ fontSize:11.5, color:T.amber, marginTop:8 }}>
                  ⚠️ No employees found. Make sure employees are added in the system.
                </div>
              )}
            </div>
          </Section>

          <Section title="📝 Notes & Feedback">
            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Notes</label>
              <textarea value={form.notes}
                onChange={e=>set("notes",e.target.value)}
                placeholder="Pre-interview notes or requirements..."
                rows={2} style={{ ...inp(), resize:"vertical", lineHeight:1.6 }}/>
            </div>

            <div>
              <label style={lbl}>Rating</label>
              <StarRating value={Number(form.rating)||0}
                onChange={v=>set("rating",v===Number(form.rating)?0:v)}/>
              <div style={{ fontSize:11, color:T.dim, marginTop:3 }}>
                Click a star to rate (click same star to clear)
              </div>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding:"16px 24px", borderTop:`1px solid ${T.border}`,
          display:"flex", gap:10, justifyContent:"flex-end",
          background:T.card2, flexShrink:0 }}>
          <button onClick={onClose}
            style={{ padding:"9px 20px", borderRadius:9,
              border:`1px solid ${T.border}`, background:"none",
              color:T.muted, cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"9px 28px", borderRadius:9, border:"none",
              background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
              color:"#fff", cursor:saving?"not-allowed":"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800,
              opacity:saving?.7:1 }}>
            {saving?"Saving...":(mode==="create"?"Schedule Interview":"Update Interview")}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══ OFFER LETTER MODAL ══════════════════════════════════════════════ */
const OfferLetterModal = ({ iv, authHdr, onClose, flash }) => {
  const { T } = useTheme();
  // const API_BASE = window.API_BASE || "http://localhost:8000";

  const [step,         setStep]         = React.useState("form"); // form | preview
  const [templates,    setTemplates]    = React.useState([]);
  const [sending,      setSending]      = React.useState(false);
  const [previewHtml,  setPreviewHtml]  = React.useState("");
  const [emailSent,    setEmailSent]    = React.useState(false);

  const [form, setForm] = React.useState({
    template_type:       "standard",
    candidate_name:      `${iv.first_name||""} ${iv.last_name||""}`.trim(),
    position:            iv.position || "",
    company_name:        "",
    company_address:     "",
    start_date:          "",
    salary:              "",
    reporting_to:        "",
    work_location:       "On-site",
    duration:            "3 months",
    hr_name:             "",
    hr_email:            "",
    acceptance_deadline: "",
    send_email:          false,
  });

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  React.useEffect(()=>{
    axios.get(`${API_BASE}/interviews-v2/offer-templates`, { headers: authHdr })
      .then(r=>setTemplates(r.data||[]))
      .catch(()=>setTemplates([{id:"standard",name:"Standard Offer Letter"},{id:"internship",name:"Internship Offer Letter"}]));
  },[]);

  const generate = async (send=false) => {
    setSending(true);
    try {
      const payload = { ...form, send_email: send };
      const res = await axios.post(
        `${API_BASE}/interviews-v2/${iv.id}/offer-letter`,
        payload, { headers: authHdr }
      );
      setPreviewHtml(res.data.html);
      if (send && res.data.email_sent) {
        setEmailSent(true);
        flash("✅ Offer letter sent to " + res.data.candidate_email);
      }
      setStep("preview");
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Failed to generate offer letter"));
    } finally { setSending(false); }
  };

  const printLetter = () => {
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>Offer Letter</title></head><body>${previewHtml}</body></html>`);
    w.document.close(); w.print();
  };

  const fld = (label, key, type="text", placeholder="") => (
    <div style={{ marginBottom:12 }}>
      <label style={{ fontSize:11, fontWeight:700, color:T.muted,
        textTransform:"uppercase", letterSpacing:"0.07em",
        display:"block", marginBottom:4 }}>{label}</label>
      <input type={type} value={form[key]} placeholder={placeholder}
        onChange={e=>setF(key,e.target.value)}
        style={{ width:"100%", padding:"8px 12px", border:`1px solid ${T.border}`,
          borderRadius:7, background:T.inp_bg, color:T.text,
          fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none",
          boxSizing:"border-box" }}/>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)",
      zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center",
      padding:16, backdropFilter:"blur(5px)" }}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
        borderRadius:16, width:"100%", maxWidth:step==="preview"?780:600,
        maxHeight:"92vh", display:"flex", flexDirection:"column",
        boxShadow:T.shadow }}>

        {/* Header */}
        <div style={{ padding:"18px 24px 14px", borderBottom:`1px solid ${T.border}`,
          background:`linear-gradient(135deg,${T.green}12,transparent)`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          flexShrink:0 }}>
          <div>
            <div style={{ fontSize:11, color:T.green, fontWeight:700,
              letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>
              {step==="form" ? "GENERATE OFFER LETTER" : "PREVIEW"}
            </div>
            <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:T.text }}>
              {iv.first_name} {iv.last_name}
              <span style={{ color:T.muted, fontWeight:400, fontSize:13 }}>
                {" "}· {iv.position||"Candidate"}
              </span>
            </h3>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {step==="preview" && (
              <button onClick={()=>setStep("form")}
                style={{ padding:"6px 14px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700 }}>
                ← Edit
              </button>
            )}
            <button onClick={onClose}
              style={{ border:"none", background:T.toggle_bg,
                borderRadius:"50%", width:30, height:30, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                color:T.muted }}>
              <X size={14}/>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY:"auto", flex:1, padding:"20px 24px" }}>

          {step==="form" && (
            <>
              {/* Template selector */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                  textTransform:"uppercase", letterSpacing:"0.07em",
                  display:"block", marginBottom:6 }}>Template</label>
                <div style={{ display:"flex", gap:8 }}>
                  {(templates.length ? templates : [
                    {id:"standard",name:"Standard"},
                    {id:"internship",name:"Internship"},
                  ]).map(t=>(
                    <button key={t.id} onClick={()=>setF("template_type",t.id)}
                      style={{ padding:"7px 16px", borderRadius:8, cursor:"pointer",
                        fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700,
                        border:`1px solid ${form.template_type===t.id?T.green:T.border}`,
                        background:form.template_type===t.id?`${T.green}1A`:"none",
                        color:form.template_type===t.id?T.green:T.muted }}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>{fld("Candidate Name",    "candidate_name")}</div>
                <div>{fld("Position",          "position")}</div>
                <div>{fld("Company Name",      "company_name",  "text","e.g. Hirality Pvt Ltd")}</div>
                <div>{fld("Company Address",   "company_address","text","e.g. Chennai, Tamil Nadu")}</div>
                <div>{fld("Start Date",        "start_date",    "date")}</div>
                <div>{fld("Salary / CTC",      "salary",        "text","e.g. ₹6,00,000 p.a.")}</div>
                <div>{fld("Reporting To",      "reporting_to",  "text","e.g. Engineering Manager")}</div>
                <div>{fld("Work Location",     "work_location", "text","On-site / Remote / Hybrid")}</div>
                {form.template_type==="internship" && (
                  <div>{fld("Duration","duration","text","e.g. 3 months")}</div>
                )}
                <div>{fld("HR Name",           "hr_name",       "text","e.g. Priya Rajan")}</div>
                <div>{fld("HR Email",          "hr_email",      "email")}</div>
                <div>{fld("Acceptance Deadline","acceptance_deadline","date")}</div>
              </div>

              {/* Send email toggle */}
              {iv.email && (
                <div style={{ marginTop:8, padding:"12px 14px",
                  background:`${T.green}0D`,
                  border:`1px solid ${T.green}26`, borderRadius:9,
                  display:"flex", alignItems:"center", gap:10 }}>
                  <input type="checkbox" id="sendEmail"
                    checked={form.send_email}
                    onChange={e=>setF("send_email",e.target.checked)}
                    style={{ width:16, height:16, cursor:"pointer" }}/>
                  <label htmlFor="sendEmail"
                    style={{ fontSize:13, color:T.text, cursor:"pointer" }}>
                    Send directly to candidate email:
                    <span style={{ color:T.cyan, marginLeft:5 }}>{iv.email}</span>
                  </label>
                </div>
              )}
              {!iv.email && (
                <div style={{ marginTop:8, fontSize:12, color:T.amber }}>
                  ⚠️ No email on record — add candidate email to enable direct sending
                </div>
              )}
            </>
          )}

          {step==="preview" && (
            <div>
              {emailSent && (
                <div style={{ marginBottom:14, padding:"10px 16px",
                  background:`${T.green}14`,
                  border:`1px solid ${T.green}33`, borderRadius:8,
                  fontSize:13, color:T.green, fontWeight:600 }}>
                  ✅ Offer letter emailed to {iv.email}
                </div>
              )}
              {/* Letter preview in white box — intentionally fixed paper palette */}
              <div style={{ background:"#fff", borderRadius:10, padding:8,
                border:`1px solid ${T.border}` }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}/>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:`1px solid ${T.border}`,
          display:"flex", gap:10, justifyContent:"flex-end",
          background:T.card2, flexShrink:0 }}>
          {step==="form" && (
            <>
              <button onClick={onClose}
                style={{ padding:"8px 18px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                Cancel
              </button>
              <button onClick={()=>generate(false)} disabled={sending}
                style={{ padding:"8px 20px", borderRadius:8,
                  border:`1px solid ${T.green}30`, background:`${T.green}14`,
                  color:T.green, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                {sending ? "Generating…" : "Preview Letter"}
              </button>
              {form.send_email && iv.email && (
                <button onClick={()=>generate(true)} disabled={sending}
                  style={{ padding:"8px 20px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.green},${T.cyan})`,
                    color:"#fff", cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800 }}>
                  {sending ? "Sending…" : "Generate & Send"}
                </button>
              )}
            </>
          )}
          {step==="preview" && (
            <>
              <button onClick={printLetter}
                style={{ padding:"8px 18px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                🖨️ Print / Save PDF
              </button>
              {!emailSent && iv.email && (
                <button onClick={()=>generate(true)} disabled={sending}
                  style={{ padding:"8px 20px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.green},${T.cyan})`,
                    color:"#fff", cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800 }}>
                  {sending ? "Sending…" : "📧 Send to Candidate"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ══ MAIN COMPONENT ══════════════════════════════════════════════════ */
const InterviewList = ({ token }) => {
  const { T } = useTheme();
  const STATUS_COLOR = useStatusColor();
  const TYPE_COLOR = useTypeColor();
  const { id } = useParams();
  const authHdr = { Authorization: `Bearer ${token}` };

  const [interviews, setInterviews] = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [technology, setTechnology] = useState({});
  const [loading,    setLoading]    = useState(false);
  const [activeTab,  setActiveTab]  = useState("scheduled");
  const [search,     setSearch]     = useState("");
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState("");

  const [modal,      setModal]      = useState(null); // null | "create" | "edit" | "view"
  const [current,    setCurrent]    = useState(null);
  const [linkIv,     setLinkIv]     = useState(null); // interview for meeting links popup
  const [offerIv,    setOfferIv]    = useState(null); // interview for offer letter
  const [converting, setConverting] = useState(null); // interview id being converted
  const [converted,  setConverted]  = useState({});   // {id: true} map of converted
  const [editData,   setEditData]   = useState(null);
  const [delId,      setDelId]      = useState(null);
  const [delOpen,    setDelOpen]    = useState(false);

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""), 3500); };

  const convertToEmployee = async (iv) => {
    setConverting(iv.id);
    try {
      const h = { headers: authHdr };
      const payload = {
        first_name:     iv.first_name,
        last_name:      iv.last_name  || "",
        email:          iv.email      || "",
        phone:          iv.phone      || "",
        date_of_joining:new Date().toISOString().split("T")[0],
        status:         "Active",
      };
      const empRes = await axios.post(`${API_BASE}/employees/`, payload, h);
      const empId  = empRes.data?.id;
      // Update interview status
      await axios.put(`${API_BASE}/interviews-v2/${iv.id}`,
        { status:"SELECTED", notes:`Converted to employee ID: ${empId}` }, h
      ).catch(()=>{});
      setConverted(prev => ({...prev, [iv.id]: true}));
      flash(`✅ ${iv.first_name} ${iv.last_name} added as employee`);
      fetchAll();
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Conversion failed"));
    } finally { setConverting(null); }
  };

  const fetchAll = async () => {
    setLoading(true);

    // Fetch employees independently — never block on interview errors
    axios.get(`${API_BASE}/interviews-v2/employees`, { headers: authHdr })
      .then(r => setEmployees(r.data || []))
      .catch(e => console.error("Employees fetch failed:", e));

    // Fetch technology name
    axios.get(APIConstant.getTechnologies, { headers: authHdr })
      .then(r => setTechnology((r.data||[]).find(t=>t.id==id) || {}))
      .catch(()=>{});

    // Fetch interviews
    try {
      const intRes = await axios.get(
        `${API_BASE}/interviews-v2/?technology_id=${id}`,
        { headers: authHdr }
      );
      setInterviews(intRes.data || []);
    } catch(e) {
      console.error("Interviews fetch error:", e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{ fetchAll(); }, []);

  const openCreate = () => {
    setEditData({ ...EMPTY, technology_id: Number(id) });
    setModal("create");
  };

  const openEdit = (iv) => {
    setEditData({
      ...iv,
      interviewer_id: iv.interviewer_id || "",
      rating:         iv.rating         || "",
      interview_date: iv.interview_date || "",
      interview_time: iv.interview_time || "",
      technology_id:  Number(id),
      room_name:      iv.room_name      || "",
    });
    setModal("edit");
  };

  const openView = (iv) => { setCurrent(iv); setModal("view"); };

  const handleSave = async (form) => {
    if(!form.first_name?.trim()) return flash("❌ First name is required");
    if(!form.last_name?.trim())  return flash("❌ Last name is required");
    setSaving(true);
    const payload = {
      ...form,
      technology_id:  Number(id),
      interviewer_id: form.interviewer_id ? Number(form.interviewer_id) : null,
      rating:         form.rating         ? Number(form.rating)         : null,
      interview_date: form.interview_date || null,
      interview_time: form.interview_time || null,
    };
    try {
      if(modal==="create") {
        await axios.post(`${API_BASE}/interviews-v2/`, payload, { headers: authHdr });
        flash("✅ Interview scheduled");
      } else {
        await axios.put(`${API_BASE}/interviews-v2/${editData.id}`, payload, { headers: authHdr });
        flash("✅ Interview updated");
      }
      setModal(null); fetchAll();
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Failed")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/interviews-v2/${delId}`, { headers: authHdr });
      flash("✅ Deleted"); setDelOpen(false); setDelId(null); fetchAll();
    } catch { flash("❌ Delete failed"); }
  };

  const updateStatus = async (iv, newStatus) => {
    try {
      await axios.put(`${API_BASE}/interviews-v2/${iv.id}`, {
        ...iv, status:newStatus, technology_id:Number(id),
        interviewer_id:iv.interviewer_id||null, rating:iv.rating||null,
      }, { headers: authHdr });
      flash(`✅ Marked as ${newStatus}`); fetchAll();
    } catch { flash("❌ Update failed"); }
  };

  // Tabs
  const scheduled = interviews.filter(i=>i.status==="Scheduled");
  const completed  = interviews.filter(i=>i.status==="Completed");
  const cancelled  = interviews.filter(i=>i.status==="Cancelled");
  const noShow     = interviews.filter(i=>i.status==="No Show");

  const selected_cands = interviews.filter(i=>i.status==="SELECTED");
  const TABS = [
    { key:"scheduled", label:"Scheduled", count:scheduled.length,        cls:styles.active },
    { key:"completed", label:"Completed", count:completed.length,        cls:styles.activeGreen },
    { key:"selected",  label:"✅ Selected",count:selected_cands.length,  cls:styles.activeGreen },
    { key:"cancelled", label:"Cancelled", count:cancelled.length,        cls:styles.activeRed },
    { key:"noshow",    label:"No Show",   count:noShow.length,           cls:styles.activeAmber },
  ];

  const rows = ({
    scheduled, completed, cancelled,
    noshow: noShow,
    selected: selected_cands,
  })[activeTab] || [];

  const filteredRows = rows.filter(iv=>{
    const q = search.toLowerCase();
    return !search ||
      `${iv.first_name} ${iv.last_name}`.toLowerCase().includes(q) ||
      (iv.position||"").toLowerCase().includes(q) ||
      (iv.email||"").toLowerCase().includes(q);
  });

  return (
    <div className={styles.pageRoot}>

      {/* Breadcrumb */}
      <Breadcrumbs className={styles.breadcrumb} aria-label="breadcrumb">
        <Link to="/admin/interviews" style={{ textDecoration:"none" }}>Interviews</Link>
        <Typography style={{ fontSize:12, color:T.muted }}>
          {technology.name || "List"}
        </Typography>
      </Breadcrumbs>

      <div className={styles.interviewWrapper}>

        {/* Header */}
        <div className={styles.interviewHeader}>
          <div className={styles.interviewHeaderLeft}>
            <h3>{technology.name || "—"} — Interviews</h3>
            <p>{interviews.length} total interviews</p>
          </div>
          <button className={styles.btnPrimary} onClick={openCreate}>
            <Plus size={14}/> Schedule Interview
          </button>
        </div>

        {/* ── TABS ── */}
        <div className={styles.tabsWrap} style={{ padding:"0 20px" }}>
          {TABS.map(tab=>(
            <button key={tab.key}
              onClick={()=>setActiveTab(tab.key)}
              className={`${styles.tab} ${activeTab===tab.key ? tab.cls : ""}`}
              style={{ display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" }}>
              {tab.label}
              <span className={styles.tabCount}>{tab.count}</span>
            </button>
          ))}
        </div>

        <div className={styles.cardBody}>

          {/* Search */}
          <div style={{ position:"relative", marginBottom:16, maxWidth:400 }}>
            <Search size={13} style={{ position:"absolute", left:11, top:"50%",
              transform:"translateY(-50%)", color:T.dim, zIndex:1 }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search candidate, position, email..."
              style={{ width:"100%", padding:"9px 12px 9px 34px",
                border:`1px solid ${T.border}`,
                borderRadius:8, background:T.inp_bg,
                color:T.text, fontFamily:"Outfit,sans-serif",
                fontSize:13, outline:"none", boxSizing:"border-box" }}/>
          </div>

          {loading ? (
            <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.darkTable}>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Contact</th>
                    <th>Position</th>
                    <th>Type</th>
                    <th>Date &amp; Time</th>
                    <th>Interviewer</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length===0 ? (
                    <tr className={styles.emptyRow}>
                      <td colSpan={9}>No interviews found</td>
                    </tr>
                  ) : filteredRows.map(iv=>{
                    const sc = STATUS_COLOR[iv.status]||T.muted;
                    const tc = TYPE_COLOR[iv.interview_type]||T.muted;
                    return (
                      <tr key={iv.id}>
                        <td>
                          <div style={{ fontWeight:700, color:T.text }}>
                            {iv.first_name} {iv.last_name}
                          </div>
                          {iv.id_number && (
                            <div style={{ fontSize:11, color:T.dim }}>
                              ID: {iv.id_number}
                            </div>
                          )}
                        </td>
                        <td className={styles.dimTd}>
                          {iv.email && (
                            <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:12 }}>
                              <Mail size={10}/>{iv.email}
                            </div>
                          )}
                          {iv.phone && (
                            <div style={{ display:"flex", alignItems:"center", gap:4,
                              fontSize:12, marginTop:2 }}>
                              <Phone size={10}/>{iv.phone}
                            </div>
                          )}
                        </td>
                        <td className={styles.dimTd} style={{ maxWidth:130,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {iv.position||"—"}
                        </td>
                        <td>
                          <span style={{ fontSize:11, padding:"2px 9px", borderRadius:100,
                            background:`${tc}18`, color:tc, fontWeight:700 }}>
                            {iv.interview_type}
                          </span>
                        </td>
                        <td className={styles.dimTd}>
                          {iv.interview_date ? (
                            <>
                              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                <Calendar size={10}/>{iv.interview_date}
                              </div>
                              {iv.interview_time && (
                                <div style={{ display:"flex", alignItems:"center",
                                  gap:4, fontSize:11, color:T.dim, marginTop:2 }}>
                                  <Clock size={10}/>{iv.interview_time}
                                </div>
                              )}
                            </>
                          ) : "—"}
                        </td>
                        <td className={styles.dimTd}>{iv.interviewer_name||"—"}</td>
                        <td>
                          {iv.rating ? (
                            <span>
                              {[1,2,3,4,5].map(n=>(
                                <span key={n} style={{ color:n<=iv.rating?T.amber:T.dim }}>★</span>
                              ))}
                            </span>
                          ) : <span style={{ color:T.dim }}>—</span>}
                        </td>
                        <td style={{ padding:"12px 14px" }}>
                          {["SELECTED","NOT-SELECTED","ONHOLD"].includes(iv.status) ? (
                            <span style={{ fontSize:11, padding:"4px 10px",
                              borderRadius:100, border:`1px solid ${sc}40`,
                              background:`${sc}15`, color:sc, fontWeight:700,
                              whiteSpace:"nowrap" }}>
                              {iv.status==="NOT-SELECTED"?"Rejected":
                               iv.status==="ONHOLD"    ?"On Hold":"Selected ✓"}
                            </span>
                          ) : (
                            <select value={iv.status}
                              onChange={e=>updateStatus(iv,e.target.value)}
                              style={{ fontSize:11, padding:"3px 8px",
                                borderRadius:100, border:`1px solid ${sc}40`,
                                background:`${sc}15`, color:sc, fontWeight:700,
                                cursor:"pointer", outline:"none",
                                fontFamily:"Outfit,sans-serif" }}>
                              {["Scheduled","Completed","Cancelled","No Show"].map(s=>(
                                <option key={s} style={{background:T.opt_bg,color:T.text}}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td style={{ padding:"12px 14px" }}>
                          <button
                            onClick={()=>setLinkIv(iv)}
                            title="Get meeting links"
                            style={{ display:"flex", alignItems:"center", gap:5,
                              padding:"5px 10px", borderRadius:7,
                              border:`1px solid ${T.green}4D`,
                              background:`${T.green}0F`,
                              color:T.green, cursor:"pointer",
                              fontFamily:"Outfit,sans-serif", fontSize:11.5,
                              fontWeight:700, whiteSpace:"nowrap" }}>
                            📹 Links
                          </button>
                        </td>
                        <td>
                          <div className={styles.actions}>
                            <button className={`${styles.iconBtn} ${styles.viewBtn}`}
                              onClick={()=>openView(iv)} title="View details">
                              <Eye size={13}/>
                            </button>
                            <button className={`${styles.iconBtn} ${styles.editBtn}`}
                              onClick={()=>openEdit(iv)} title="Edit">
                              <Edit2 size={13}/>
                            </button>

                            {iv.status==="SELECTED" && (
                              <>
                                <button
                                  onClick={()=>setOfferIv(iv)}
                                  title="Generate Offer Letter"
                                  style={{ width:30, height:30, borderRadius:7,
                                    border:`1px solid ${T.green}4D`,
                                    background:`${T.green}14`,
                                    color:T.green, cursor:"pointer",
                                    display:"flex", alignItems:"center",
                                    justifyContent:"center", fontSize:14 }}>
                                  📄
                                </button>
                                {!converted[iv.id] ? (
                                  <button
                                    onClick={()=>convertToEmployee(iv)}
                                    disabled={converting===iv.id}
                                    title="Move to Employee"
                                    style={{ width:30, height:30, borderRadius:7,
                                      border:`1px solid ${T.purple}4D`,
                                      background:`${T.purple}14`,
                                      color:T.purple, cursor:"pointer",
                                      display:"flex", alignItems:"center",
                                      justifyContent:"center", fontSize:14,
                                      opacity:converting===iv.id?.6:1 }}>
                                    {converting===iv.id ? "⏳" : "👤"}
                                  </button>
                                ) : (
                                  <span title="Already added as employee"
                                    style={{ width:30, height:30, borderRadius:7,
                                      border:`1px solid ${T.green}4D`,
                                      background:`${T.green}14`,
                                      color:T.green, display:"flex",
                                      alignItems:"center", justifyContent:"center",
                                      fontSize:14 }}>
                                    ✓
                                  </span>
                                )}
                              </>
                            )}
                            <button className={`${styles.iconBtn} ${styles.delBtn}`}
                              onClick={()=>{ setDelId(iv.id); setDelOpen(true); }} title="Delete">
                              <Trash2 size={13}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(modal==="create"||modal==="edit") && (
        <ScheduleModal
          mode={modal}
          data={editData}
          employees={employees}
          techName={technology.name}
          onClose={()=>setModal(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {/* ── View Modal ── */}
      {modal==="view" && (
        <ViewPanel
          iv={current}
          onClose={()=>setModal(null)}
          onEdit={(iv)=>{ setModal(null); openEdit(iv); }}
        />
      )}

      {/* ── Delete Confirm ── */}
      <Popup
        open={delOpen}
        message="Are you sure you want to delete this interview?"
        handleClose={()=>{ setDelOpen(false); setDelId(null); }}
        handleConfirm={handleDelete}
      />

      {/* Meeting Links Modal */}
      {linkIv && (
        <MeetingLinksModal
          iv={linkIv}
          techId={id}
          onClose={()=>setLinkIv(null)}
        />
      )}

      {/* Offer Letter Generator */}
      {offerIv && (
        <OfferLetterGenerator
          iv={offerIv}
          authHdr={authHdr}
          onClose={()=>setOfferIv(null)}
          flash={flash}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%",
          transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default InterviewList;
